import { Link as InertiaLink } from '@inertiajs/react'

import { Link } from '@atoms'
import Badge from '@components/atoms/Badge'
import { PageLayout } from '@components/layouts/PageLayout'
import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'
import type { SettingsHeader } from '@components/layouts/PageHeaderIsland'
import { PageHeaderNav } from '@molecules/PageHeaderNav'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('embeds.index')

type EmbedId = 'events_map' | 'rsvp_form' | 'groups_map' | 'group_join_form'

export type EmbedsIndexProps = {
  crm: string | null
  embeds: Array<{ id: EmbedId; href: string | null }>
  settingsHeader: SettingsHeader
}

type EmbedCardContent = {
  icon: IconTypes
  description: string
  note?: string
  badge?: { label: string; color: 'success' | 'info' | 'muted' }
}

function cardContent(id: EmbedId, isNationBuilder: boolean): EmbedCardContent {
  switch (id) {
    case 'events_map':
      return {
        icon: 'map-01',
        description: t('events_map.description'),
        note: t('any_website'),
        badge: { label: t('badges.new'), color: 'success' },
      }
    case 'rsvp_form':
      return {
        icon: 'calendar-check-01',
        description: isNationBuilder ? t('rsvp_form.description_nationbuilder') : t('rsvp_form.description'),
        note: isNationBuilder ? t('rsvp_form.works_on_nationbuilder') : t('any_website'),
        badge: isNationBuilder ? { label: t('badges.moved_from_nationbuilder'), color: 'info' } : undefined,
      }
    case 'groups_map':
      return { icon: 'users-01', description: t('groups_map.description'), note: t('any_website') }
    case 'group_join_form':
      return { icon: 'user-plus-01', description: t('group_join_form.description') }
  }
}

function EmbedCard({ id, href, isNationBuilder }: { id: EmbedId; href: string | null; isNationBuilder: boolean }) {
  const name = t(`${id}.name`)
  const content = cardContent(id, isNationBuilder)
  const { icon, description, note } = content
  const planned = !href
  const badge = planned ? { label: t('badges.planned'), color: 'muted' as const } : content.badge

  return (
    <article
      className={cn(
        'tw:flex tw:flex-col tw:gap-4 tw:rounded-xl tw:border tw:p-6',
        planned
          ? 'tw:border-dashed tw:border-gray-300 tw:bg-gray-cool-50'
          : 'tw:border-gray-200 tw:bg-white tw:shadow-xs'
      )}
    >
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <span
          className={cn(
            'tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-lg',
            planned ? 'tw:bg-white tw:text-gray-400' : 'tw:bg-primary-50 tw:text-primary-600'
          )}
        >
          <Icon type={icon} className="tw:h-5 tw:w-5" />
        </span>
        {badge && <Badge color={badge.color}>{badge.label}</Badge>}
      </div>
      <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
        <h2
          className={cn('tw:m-0 tw:text-lg tw:font-semibold', planned ? 'tw:text-gray-500' : 'tw:text-gray-cool-900')}
        >
          {name}
        </h2>
        <p className={cn('tw:m-0 tw:text-sm', planned ? 'tw:text-gray-500' : 'tw:text-gray-cool-600')}>{description}</p>
      </div>
      {note && (
        <p className="tw:m-0 tw:flex tw:items-start tw:gap-1.5 tw:text-xs tw:text-gray-true-500">
          <Icon type="globe-01" className="tw:mt-px tw:h-3.5 tw:w-3.5 tw:shrink-0" />
          {note}
        </p>
      )}
      {href && (
        <div>
          <Link as="inertiaLink" href={href} rank="secondary" size="sm" ariaLabel={t('get_code_for', { name })}>
            {t('get_code')}
          </Link>
        </div>
      )}
    </article>
  )
}

export default function EmbedsIndex({
  crm,
  embeds,
  settingsHeader: { heading, subtitle, icon, pageTabs },
}: EmbedsIndexProps) {
  const isNationBuilder = crm === 'nationbuilder'

  return (
    <PageLayout.Root>
      <PageLayout.Header
        heading={heading}
        icon={icon}
        subtitle={subtitle}
        nav={pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={InertiaLink} />}
      />
      <PageLayout.Body>
        <div className="tw:grid tw:gap-4 tw:md:grid-cols-2 tw:xl:grid-cols-3">
          {embeds.map(({ id, href }) => (
            <EmbedCard key={id} id={id} href={href} isNationBuilder={isNationBuilder} />
          ))}
        </div>
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
